import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { Button, Color, Flex, Icon, InfoTooltip, Modal, SubgridTable, Text } from "src/elements";
import { importPlanogramsModalDataAtom, ImportPlanogramsModalData } from "./store/atoms";
import { FileUploader, Tooltip } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { useMerchflowsApi } from "src/api/merchflows";
import { useAsyncState } from "../../hooks/useAsyncState";
import {
  ResponsePostMerchflowsImportPlanograms,
  RoutePostMerchflowsImportPlanograms,
} from "@CommonApi/merchflows/import-planograms";
import { useModals } from "src/components/Modals";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { routeToUrl } from "@CommonUtils/routes";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";
import styled from "styled-components";
import { downloadFile } from "src/utils/downloadFile";
import { useHoverClickable } from "src/utils/hoverClickable";

const CsvFormatTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
`;

const CsvFormatTableHeader = styled.th`
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  font-weight: bold;
`;

const CsvFormatTableCell = styled.td`
  padding: 6px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  vertical-align: top;
`;

const CsvFormatTableRow = styled.tr`
  &:last-child ${CsvFormatTableCell} {
    border-bottom: none;
  }
`;

const DownloadIconWrapper = styled(Flex).attrs<React.HTMLAttributes<HTMLDivElement>>((props) => ({
  role: props.role || "button",
  "aria-label": props["aria-label"],
}))<React.HTMLAttributes<HTMLDivElement>>`
  position: relative;
  cursor: pointer;
`;

const SAMPLE_CSV_DATA = `store_code,category_code,product_code,segment,shelf,order,facings
309,HEALTH BEVERAGE,13058254,1,1,1,1
309,HEALTH BEVERAGE,13266798,1,1,2,1
309,HEALTH BEVERAGE,13253434,1,1,3,1
309,HEALTH BEVERAGE,13230946,1,1,4,1
309,HEALTH BEVERAGE,13102871,1,1,5,1
309,HEALTH BEVERAGE,13135484,1,3,1,1
309,HEALTH BEVERAGE,11838626,1,3,2,1
309,HEALTH BEVERAGE,13039613,1,3,3,1
309,HEALTH BEVERAGE,13147486,1,3,4,1
309,HEALTH BEVERAGE,11061335,1,3,5,2
309,HEALTH BEVERAGE,13244236,1,3,6,1
309,HEALTH BEVERAGE,13068607,1,3,7,1
309,HEALTH BEVERAGE,12013140,1,4,1,1
309,HEALTH BEVERAGE,13007053,1,4,2,1
309,HEALTH BEVERAGE,13025133,1,4,3,1
309,HEALTH BEVERAGE,13058371,1,4,4,1
309,HEALTH BEVERAGE,13250815,1,4,5,1
309,HEALTH BEVERAGE,13129720,1,4,6,1
309,HEALTH BEVERAGE,13256730,1,5,1,1
309,HEALTH BEVERAGE,12201934,1,5,2,1
309,HEALTH BEVERAGE,13109456,1,5,3,1
309,HEALTH BEVERAGE,13109459,1,5,4,1
309,HEALTH BEVERAGE,11633110,1,5,5,1
309,HEALTH BEVERAGE,13239862,1,6,1,1
309,HEALTH BEVERAGE,11733103,1,6,2,1
309,HEALTH BEVERAGE,11484848,1,6,3,1
309,HEALTH BEVERAGE,13041738,1,6,4,1
309,HEALTH BEVERAGE,12239692,1,6,5,1
309,HEALTH BEVERAGE,308454,1,6,6,1`;

const columns: SubgridColumn<ResponsePostMerchflowsImportPlanograms["store_results"][0]>[] = [
  {
    id: "store_code",
    header: "Store Code",
    justify: "left",
  },
  {
    id: "status",
    header: "Status",
    justify: "center",
    renderer: ({ value }) => {
      const isSuccess = value === "SUCCESS";
      return (
        <Flex align="center" gap="5px">
          <Icon
            name={isSuccess ? "checkmarkCircle" : "clearCircle"}
            color={isSuccess ? Color.primary : Color.red}
            size={14}
          />
          <Text variant="small2">{value}</Text>
        </Flex>
      );
    },
  },
  {
    id: "error",
    header: "Error",
    justify: "left",
    renderer: ({ value }) => (
      <Text variant="small2" color={value ? Color.red : Color.textSecondary}>
        {value || "-"}
      </Text>
    ),
  },
];

export const ModalImportPlanograms = () => {
  const [data, setData] = useAtom(importPlanogramsModalDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, [setData]);

  if (!data) {
    return null;
  }

  return <ModalImportPlanogramsInner flowData={data} />;
};

interface Props {
  flowData: ImportPlanogramsModalData;
}

const ModalImportPlanogramsInner = ({ flowData }: Props) => {
  const { hasLoader } = useLoaders();
  const { postMerchflowImportPlanogramsApi } = useMerchflowsApi();
  const { closeModal } = useModals();
  const { navigate } = useNavigation();

  const [planogramFile, setPlanogramFile] = useState<File | null>(null);
  const [fileUploaderKey, setFileUploaderKey] = useState(0);
  const [response, setResponse] = useAsyncState<ResponsePostMerchflowsImportPlanograms | null>(
    null,
  );

  const isImportPlanogramsLoading = hasLoader(RoutePostMerchflowsImportPlanograms);

  const refDownloadIcon = useRef<HTMLDivElement>(null);
  const { isHovered } = useHoverClickable(refDownloadIcon);

  const handleDownloadSample = () => {
    downloadFile({
      name: "planogram-import-sample",
      extension: "csv",
      data: SAMPLE_CSV_DATA,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleDownloadSample();
    }
  };

  const handleViewStores = () => {
    if (!response || !response.merchflow_id) return;
    closeModal("ModalImportPlanograms");
    closeModal("ModalMerchflowReview");
    navigate(
      routeToUrl(pages.merchflows_merchflowId_StoresAll, {
        merchflowId: response.merchflow_id,
      }),
    );
  };

  const importPlanograms = async () => {
    if (!planogramFile) return;

    const importResponse = await postMerchflowImportPlanogramsApi({
      planogram_file: planogramFile,
      merchflow_name: flowData.name,
      go_live_date: flowData.goLiveDate.toISOString(),
      core_range_option: flowData.coreRange,
      notes: flowData.notes || "",
      ...(flowData.deactivationDate && {
        deactivation_date: flowData.deactivationDate.toISOString(),
      }),
    });
    setResponse(importResponse);

    if (importResponse && importResponse.successful_stores === 0) {
      setPlanogramFile(null);
      setFileUploaderKey((k) => k + 1);
    }
  };

  const hasSuccessfulStores = Boolean(response && response.successful_stores > 0);

  return (
    <Modal name="ModalImportPlanograms" title="Import CSV for New Flow" width="700px">
      <Flex padding="10px" column gap="16px" flexGrow minHeight="0" overflow="hidden">
        <Flex align="center" gap="5px">
          <Text variant="body1">CSV Format Guide</Text>
          <InfoTooltip
            tooltip={
              <Tooltip width="500px" textAlign="left" whiteSpace="wrap">
                <div>
                  <b>Required CSV Columns:</b>
                  <CsvFormatTable>
                    <thead>
                      <tr>
                        <CsvFormatTableHeader>Column</CsvFormatTableHeader>
                        <CsvFormatTableHeader>Description</CsvFormatTableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>store_code</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>Store code from fixtures loaded</CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>category_code</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>Category code set up</CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>product_code</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>Product code from PM data</CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>segment</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>Bay number from fixture data</CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>shelf</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>
                          Location of item on a bay (shelf number from fixture data)
                        </CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>order</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>
                          Location of item on a shelf. 1 for leftmost on a shelf. Shelves can be
                          overfilled, sequence restarts on next shelf.
                        </CsvFormatTableCell>
                      </CsvFormatTableRow>
                      <CsvFormatTableRow>
                        <CsvFormatTableCell>
                          <b>facings</b>
                        </CsvFormatTableCell>
                        <CsvFormatTableCell>Number of facings of an item</CsvFormatTableCell>
                      </CsvFormatTableRow>
                    </tbody>
                  </CsvFormatTable>
                </div>
              </Tooltip>
            }
          />
          <DownloadIconWrapper
            ref={refDownloadIcon}
            padding="0 3px"
            onClick={handleDownloadSample}
            onKeyDown={handleKeyDown}
            role="button"
            aria-label="Download example CSV"
            tabIndex={0}
          >
            <Icon name="arrowDown" size={12} color={isHovered ? Color.primary : Color.spaceGray} />
            <Tooltip>Download example CSV</Tooltip>
          </DownloadIconWrapper>
        </Flex>
        <Flex gap="10px">
          <FileUploader
            key={fileUploaderKey}
            fileLabel="planogram data"
            fileType="csv"
            onChange={(file) => {
              setPlanogramFile(file);
              if (file) setResponse(null);
            }}
            isLoading={isImportPlanogramsLoading}
            onSuccess={hasSuccessfulStores ? "Import completed successfully" : undefined}
          />
        </Flex>

        {response && (
          <Flex column flexGrow minHeight="0" overflow="auto" gap="10px">
            <Flex gap="10px" align="center">
              <Text variant="body2">
                Total: {response.total_stores} | Success: {response.successful_stores} | Failed:{" "}
                {response.failed_stores}
              </Text>
            </Flex>
            {!hasSuccessfulStores && (
              <Text variant="small2" color={Color.red}>
                Import finished, but no stores were imported successfully. Please try again.
              </Text>
            )}
            <SubgridTable columns={columns} data={response.store_results} />
          </Flex>
        )}

        <Flex gap="10px" justify="center">
          <Button
            isDisabled={hasSuccessfulStores ? false : !planogramFile || isImportPlanogramsLoading}
            onClick={hasSuccessfulStores ? handleViewStores : importPlanograms}
          >
            {hasSuccessfulStores ? "View Stores" : "Import"}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
